import { jsxs, jsx } from 'react/jsx-runtime';
import * as React from 'react';
import '../../layout-flex/types/index.esm.js';
import { EGDSLayoutFlex } from '../../layout-flex/layout-flex/index.esm.js';
import { EGDSLayoutFlexItem } from '../../layout-flex/layout-flex-item/index.esm.js';
import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import { useLocalization } from '@shared-ui/localization-context';
import styles from '../../styles/uitk-egds-sheet.css';
import { addSheetToStack, setFocusableElems, setElementFocus, hasSheetClassName, setFocus, removeSheetFromStack, defineSheetLayout, trapUpperBoundFocus, trapLowerBoundFocus } from '../sheet-utilities/index.esm.js';
import { classNames } from '../../utils/classnames/index.esm.js';
import { MakeEgdsSizingLength } from '../../utils/egds-sizing-length/index.esm.js';
import { liveAnnounce, Level } from '../../utils/live-announce/index.esm.js';
const getClassNameList = classNames.bind(styles);
const EGDSCenteredSheet = props => {
  const {
    ariaLabel,
    autoFocus = true,
    children,
    className,
    isVisible,
    centeredSheetSize = 'medium',
    height,
    returnFocusOnClose = true,
    triggerRef,
    focusElemRef,
    type = 'centered',
    ariaModal = true,
    ...attributes
  } = props;
  const sheetRef = React.useRef(null);
  const baseClassName = `uitk-${type}-sheet`;
  const sheetClassNames = getClassNameList(baseClassName, className, centeredSheetSize === 'small' ? `uitk-centered-sheet-small` : 'uitk-centered-sheet-medium');
  const sheetStyles = {
    ...(height && {
      [`--uitk-centered-sheet-height`]: `${MakeEgdsSizingLength(height)}`
    })
  };
  // Hooks
  React.useEffect(() => {
    addSheetToStack(sheetRef, props);
    setFocusableElems(sheetRef);
  }, []);
  // Set focus when initial mount only
  React.useEffect(() => {
    if (autoFocus && isVisible) {
      setElementFocus(sheetRef, true, focusElemRef);
    }
    // Reset liveannounce so the liveannounce element is not navigable by screenreader.
    isVisible && liveAnnounce('', Level.POLITE);
    isVisible && hasSheetClassName('add');
  }, [autoFocus, isVisible]);
  // "unmount" hook
  React.useEffect(() => () => {
    if (returnFocusOnClose) {
      setFocus(triggerRef);
    }
    removeSheetFromStack(sheetRef);
    hasSheetClassName('remove');
  }, [returnFocusOnClose, triggerRef]);
  const trapUpperFocus = () => {
    setFocusableElems(sheetRef);
    trapUpperBoundFocus(sheetRef);
  };
  const trapLowerFocus = () => {
    setFocusableElems(sheetRef);
    trapLowerBoundFocus(sheetRef);
  };
  const {
    hasCloseButton,
    layoutProps,
    closeButtonLayoutProps,
    closeButtonElem,
    childrenElem
  } = defineSheetLayout(children);
  const l10n = useLocalization();
  return jsxs("section", {
    ref: sheetRef,
    className: sheetClassNames,
    tabIndex: -1,
    "aria-modal": ariaModal,
    role: "dialog",
    "aria-label": ariaLabel ?? l10n.formatText('sheets.rootElement.ariaLabel'),
    style: sheetStyles,
    ...attributes,
    children: [jsx("div", {
      tabIndex: 0,
      onFocus: trapUpperFocus,
      "aria-hidden": "true"
    }), hasCloseButton && jsxs(EGDSLayoutFlex, {
      ...layoutProps,
      children: [jsx(EGDSLayoutFlexItem, {
        ...closeButtonLayoutProps,
        children: jsx("div", {
          children: closeButtonElem
        })
      }), childrenElem && jsx(EGDSLayoutFlexItem, {
        children: childrenElem
      })]
    }), !hasCloseButton && childrenElem, jsx("div", {
      tabIndex: 0,
      onFocus: trapLowerFocus,
      "aria-hidden": "true"
    })]
  });
};
EGDSCenteredSheet.displayName = 'EGDSCenteredSheet';
export { EGDSCenteredSheet };